import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { lesbareSchriftfarbe } from "./lesbare-schriftfarbe";
import useApiRequest from "../../lib/hooks/useRequest";
import { ICreatePatientRequest, IPatientRequestPublicLocation,
  IPatientRequestFormSettings,
} from "../../lib/interfaces/patient-request";

type Felder = {
  firstName: string;
  lastName: string;
  mail: string;
  phone: string;
  message: string;
  belongsToCompanyLocationId: string;
};

const leer: Felder = {
  firstName: "",
  lastName: "",
  mail: "",
  phone: "",
  message: "",
  belongsToCompanyLocationId: "",
};

/**
 * Das oeffentliche Anfrageformular, eingebunden auf der Website des Kunden.
 *
 * Bewusst ohne jedes Tracking: Anders als das Bewerberformular meldet hier nichts an
 * Facebook (Epic KAN-226, Verbotsliste). Es gibt auch keine Einwilligungs-Checkbox — das
 * Formular verhaelt sich wie das Bewerberformular und verlaesst sich auf den
 * Datenschutz-Link der Kundenwebsite (PRD KAN-227, entschieden am 21.10.2026).
 */
export const PatientRequestForm = () => {
  const { organizationId } = useParams();
  const { apiRequest } = useApiRequest();

  const [felder, setFelder] = useState<Felder>(leer);
  const [haeuser, setHaeuser] = useState<IPatientRequestPublicLocation[]>([]);
  // Die Produktfarbe gilt, bis die Farbe des Betriebs geladen ist.
  const [akzentfarbe, setAkzentfarbe] = useState("#0099ce");
  const [fehlendeFelder, setFehlendeFelder] = useState<string[]>([]);
  const [sendet, setSendet] = useState(false);
  const [gesendet, setGesendet] = useState(false);
  const [uebertragungFehlgeschlagen, setUebertragungFehlgeschlagen] = useState(false);

  useEffect(() => {
    const laden = async () => {
      if (!organizationId) return;
      const antwort = await apiRequest<IPatientRequestFormSettings>(
        `embed/${organizationId}/patient-request-locations`,
        "GET",
        { withoutAuth: true },
        "v2.0"
      );
      const geladen = antwort.data?.locations ?? [];
      setHaeuser(geladen);
      if (antwort.data?.color) setAkzentfarbe(antwort.data.color);
      // Ein Betrieb mit genau einem Haus soll keine Auswahl mit einem Eintrag sehen.
      if (geladen.length === 1) {
        setFelder((bisher) => ({ ...bisher, belongsToCompanyLocationId: geladen[0]._id }));
      }
    };
    void laden();
  }, [apiRequest, organizationId]);

  const gewaehltesHaus = useMemo(
    () => haeuser.find((haus) => haus._id === felder.belongsToCompanyLocationId),
    [haeuser, felder.belongsToCompanyLocationId]
  );

  const pruefen = (): string[] => {
    const fehlt: string[] = [];
    if (!felder.lastName.trim()) fehlt.push("lastName");
    if (!felder.belongsToCompanyLocationId) fehlt.push("belongsToCompanyLocationId");
    if (!felder.mail.trim() && !felder.phone.trim()) {
      fehlt.push("mail");
      fehlt.push("phone");
    }
    return fehlt;
  };

  const absenden = async (event: React.FormEvent) => {
    event.preventDefault();
    const fehlt = pruefen();
    setFehlendeFelder(fehlt);
    if (fehlt.length > 0) return;

    setSendet(true);
    setUebertragungFehlgeschlagen(false);

    const koerper: ICreatePatientRequest = {
      lastName: felder.lastName.trim(),
      belongsToCompanyLocationId: felder.belongsToCompanyLocationId,
      ...(felder.firstName.trim() ? { firstName: felder.firstName.trim() } : {}),
      ...(felder.mail.trim() ? { mail: felder.mail.trim() } : {}),
      ...(felder.phone.trim() ? { phone: felder.phone.trim() } : {}),
      ...(felder.message.trim() ? { message: felder.message.trim() } : {}),
    };

    const antwort = await apiRequest(
      `embed/${organizationId}/send-patient-request`,
      "POST",
      {
        withoutAuth: true,
        body: koerper,
        customHeaders: { "x-api-key": process.env.REACT_APP_FORM_API_KEY ?? "" },
      },
      "v2.0"
    );

    setSendet(false);
    if (antwort.error) {
      setUebertragungFehlgeschlagen(true);
      return;
    }
    setGesendet(true);
  };

  const fehlt = (feld: string): boolean => fehlendeFelder.includes(feld);
  const rahmen = (feld: string): string =>
    `w-full rounded-md border px-3 py-2 text-sm ${fehlt(feld) ? "border-red-500" : "border-gray-300"}`;

  if (gesendet) {
    return (
      <div className="mx-auto max-w-xl p-4">
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center">
          <h2 className="mb-2 text-xl font-medium">Ihre Anfrage ist angekommen</h2>
          <p className="text-sm text-gray-600">
            Wir melden uns in der Regel am nächsten Werktag bei Ihnen zurück.
            {gewaehltesHaus?.applicantsContactPhone
              ? ` Wenn es eilig ist, erreichen Sie das ${gewaehltesHaus.title} unter ${gewaehltesHaus.applicantsContactPhone}.`
              : gewaehltesHaus
                ? ` Wenn es eilig ist, rufen Sie gern direkt im ${gewaehltesHaus.title} an.`
                : ""}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl p-4">
      <form className="rounded-lg border border-gray-200 bg-white p-6" onSubmit={absenden} noValidate>
        <h2 className="mb-1 text-xl font-medium">Pflegeplatz anfragen</h2>
        <p className="mb-5 text-sm text-gray-500">Wir melden uns in der Regel am nächsten Werktag zurück.</p>

        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <label className="mb-3 block">
            <span className="mb-1 block text-sm text-gray-700">Vorname</span>
            <input
              className={rahmen("firstName")}
              value={felder.firstName}
              onChange={(event) => setFelder({ ...felder, firstName: event.target.value })}
            />
          </label>
          <label className="mb-3 block">
            <span className="mb-1 block text-sm text-gray-700">
              Nachname <span className="text-red-500">*</span>
            </span>
            <input
              className={rahmen("lastName")}
              value={felder.lastName}
              onChange={(event) => setFelder({ ...felder, lastName: event.target.value })}
            />
          </label>
          <label className="mb-3 block">
            <span className="mb-1 block text-sm text-gray-700">
              E-Mail <span className="text-red-500">*</span>
            </span>
            <input
              className={rahmen("mail")}
              type="email"
              value={felder.mail}
              onChange={(event) => setFelder({ ...felder, mail: event.target.value })}
            />
          </label>
          <label className="mb-3 block">
            <span className="mb-1 block text-sm text-gray-700">
              Telefon <span className="text-red-500">*</span>
            </span>
            <input
              className={rahmen("phone")}
              value={felder.phone}
              onChange={(event) => setFelder({ ...felder, phone: event.target.value })}
            />
          </label>
        </div>

        {/* Die Regel stand bisher nur in der Pruefung: Wer beide Felder leer liess, erfuhr
            es erst beim Absenden. Angehoerige fuellen das unter Druck aus, deshalb steht
            die Bedingung jetzt vorher da. */}
        <p className="-mt-1 mb-3 text-xs text-gray-500">Bitte mindestens eines von beiden ausfüllen, damit wir uns bei Ihnen zurückmelden können.</p>

        {haeuser.length > 0 && (
          <label className="mb-3 block">
            <span className="mb-1 block text-sm text-gray-700">
              Welcher Standort? <span className="text-red-500">*</span>
            </span>
            {/* Bei genau einem Standort steht er fest da, gesperrt: Die anfragende Person
                soll sehen, an wen ihre Anfrage geht, aber nichts zu entscheiden haben. */}
            <select
              className={`${rahmen("belongsToCompanyLocationId")} disabled:bg-gray-50 disabled:text-gray-600`}
              value={felder.belongsToCompanyLocationId}
              disabled={haeuser.length === 1}
              onChange={(event) => setFelder({ ...felder, belongsToCompanyLocationId: event.target.value })}
            >
              {haeuser.length > 1 && <option value="">Bitte wählen</option>}
              {haeuser.map((standort) => (
                <option key={standort._id} value={standort._id}>
                  {standort.title} — {standort.placeName}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="mb-3 block">
          <span className="mb-1 block text-sm text-gray-700">Ihre Nachricht</span>
          <textarea
            className={`${rahmen("message")} min-h-[88px]`}
            placeholder="Worum geht es? Ab wann wird ein Platz gesucht?"
            value={felder.message}
            onChange={(event) => setFelder({ ...felder, message: event.target.value })}
          />
        </label>

        {fehlendeFelder.length > 0 && (
          <p className="mb-3 text-sm text-red-600">
            Bitte ergänzen Sie:{" "}
            {fehlendeFelder.includes("lastName") && "Nachname"}
            {fehlendeFelder.includes("lastName") && (fehlendeFelder.includes("mail") || fehlendeFelder.includes("belongsToCompanyLocationId"))
              ? ", "
              : ""}
            {fehlendeFelder.includes("belongsToCompanyLocationId") && "den gewünschten Standort"}
            {fehlendeFelder.includes("belongsToCompanyLocationId") && fehlendeFelder.includes("mail") ? ", " : ""}
            {fehlendeFelder.includes("mail") && "E-Mail oder Telefon"}
          </p>
        )}

        {uebertragungFehlgeschlagen && (
          <p className="mb-3 text-sm text-red-600">
            Ihre Anfrage konnte gerade nicht übermittelt werden.
            {gewaehltesHaus?.applicantsContactPhone
              ? ` Bitte rufen Sie das ${gewaehltesHaus.title} direkt an: ${gewaehltesHaus.applicantsContactPhone}.`
              : " Bitte versuchen Sie es in Kürze erneut oder rufen Sie uns an."}
          </p>
        )}

        <button
          className="w-full rounded-md px-5 py-2.5 font-medium disabled:opacity-60"
          style={{ backgroundColor: akzentfarbe, color: lesbareSchriftfarbe(akzentfarbe) }}
          type="submit"
          disabled={sendet}
        >
          {sendet ? "Wird gesendet…" : "Anfrage absenden"}
        </button>

        <p className="mt-3 text-xs text-gray-500">
          Pflichtfelder sind mit * gekennzeichnet. Es wird keine Angabe zum Pflegegrad abgefragt.
        </p>
      </form>
    </div>
  );
};
